/**
 * Site Configuration
 *
 * Central source of truth for:
 * - Site metadata (name, description, URLs)
 * - Navigation structure (header, footer)
 * - Public routes (for sitemap generation)
 *
 * This config is used by:
 * - Header/Footer components for navigation
 * - SEO generation script for sitemap.xml, robots.txt
 * - llms.txt generation
 */

// =============================================================================
// Site Metadata
// =============================================================================

export const siteConfig = {
  name: 'L2Stack',
  description:
    'AI-powered CRM platform for managing contacts, companies, deals, and business workflows. Built and operated by Jezweb.',
  // Base URL - update this when deploying to production
  url: (typeof import.meta !== 'undefined' ? import.meta.env?.['VITE_SITE_URL'] : undefined) || 'https://l2stack.jezweb.com.au',
  // Author/Company
  author: {
    name: 'Jezweb',
    url: 'https://jezweb.com.au',
  },
  // Contact
  contact: {
    email: 'hello@jezweb.com.au',
  },
} as const

// =============================================================================
// Navigation Links
// =============================================================================

export interface NavItem {
  label: string
  href: string
  description?: string
  external?: boolean
}

export interface FooterLinkGroup {
  title: string
  links: NavItem[]
}

/**
 * Main navigation items (header)
 */
export const mainNavItems: NavItem[] = [
  {
    label: 'Features',
    href: '/features',
    description: 'Explore the full feature set',
  },
  {
    label: 'Technology',
    href: '/technology',
    description: 'See what powers L2Stack',
  },
  {
    label: 'Services',
    href: '/services',
    description: 'Professional services and support',
  },
]

/**
 * Footer link groups
 */
export const footerLinkGroups: FooterLinkGroup[] = [
  {
    title: 'Product',
    links: [
      { label: 'Features', href: '/features' },
      { label: 'Technology', href: '/technology' },
      { label: 'Services', href: '/services' },
    ],
  },
  {
    title: 'Resources',
    links: [
      { label: 'Documentation', href: '/docs' },
      {
        label: 'Cloudflare Workers',
        href: 'https://developers.cloudflare.com/workers/',
        external: true,
      },
    ],
  },
  {
    title: 'Company',
    links: [
      {
        label: 'Jezweb',
        href: siteConfig.author.url,
        external: true,
      },
      { label: 'Contact', href: '/contact' },
    ],
  },
]

// =============================================================================
// Public Routes (for SEO/Sitemap)
// =============================================================================

export interface PublicRoute {
  path: string
  title: string
  description: string
  /** Sitemap priority (0.0 - 1.0) */
  priority: number
  /** Sitemap change frequency */
  changefreq: 'always' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'never'
  /** Include in sitemap? */
  includeInSitemap: boolean
  /** Include in llms.txt? */
  includeInLlmsTxt: boolean
}

/**
 * All public routes that should be indexed
 *
 * Note: Dashboard routes are excluded as they require authentication
 */
export const publicRoutes: PublicRoute[] = [
  // Marketing pages
  {
    path: '/',
    title: 'Home',
    description:
      'AI-powered CRM platform for managing contacts, companies, deals, and business workflows.',
    priority: 1.0,
    changefreq: 'weekly',
    includeInSitemap: true,
    includeInLlmsTxt: true,
  },
  {
    path: '/features',
    title: 'Features',
    description:
      'Explore the complete feature set including CRM, AI integration, automation, and more.',
    priority: 0.9,
    changefreq: 'weekly',
    includeInSitemap: true,
    includeInLlmsTxt: true,
  },
  {
    path: '/technology',
    title: 'Technology',
    description:
      'Discover the modern technology stack that powers L2Stack - Cloudflare edge, AI, and more.',
    priority: 0.9,
    changefreq: 'weekly',
    includeInSitemap: true,
    includeInLlmsTxt: true,
  },
  {
    path: '/services',
    title: 'Services',
    description: 'Professional services, consulting, and support for your Cloudflare Workers projects.',
    priority: 0.8,
    changefreq: 'monthly',
    includeInSitemap: true,
    includeInLlmsTxt: true,
  },
  // Contact/Lead capture
  {
    path: '/contact',
    title: 'Contact',
    description: 'Get in touch with us for questions, support, or project inquiries.',
    priority: 0.7,
    changefreq: 'monthly',
    includeInSitemap: true,
    includeInLlmsTxt: true,
  },
  {
    path: '/get-a-quote',
    title: 'Get a Quote',
    description: 'Request a quote for custom development, consulting, or support services.',
    priority: 0.7,
    changefreq: 'monthly',
    includeInSitemap: true,
    includeInLlmsTxt: true,
  },
  // Documentation
  {
    path: '/docs',
    title: 'Documentation',
    description: 'Comprehensive documentation and guides for using L2Stack.',
    priority: 0.8,
    changefreq: 'weekly',
    includeInSitemap: true,
    includeInLlmsTxt: true,
  },
  // Auth pages (lower priority, but still indexable)
  {
    path: '/sign-in',
    title: 'Sign In',
    description: 'Sign in to your account.',
    priority: 0.3,
    changefreq: 'yearly',
    includeInSitemap: true,
    includeInLlmsTxt: false,
  },
  {
    path: '/sign-up',
    title: 'Sign Up',
    description: 'Create a new account.',
    priority: 0.4,
    changefreq: 'yearly',
    includeInSitemap: true,
    includeInLlmsTxt: false,
  },
]

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get routes for sitemap generation
 */
export function getSitemapRoutes(): PublicRoute[] {
  return publicRoutes.filter((route) => route.includeInSitemap)
}

/**
 * Get routes for llms.txt generation
 */
export function getLlmsTxtRoutes(): PublicRoute[] {
  return publicRoutes.filter((route) => route.includeInLlmsTxt)
}

/**
 * Get full URL for a path
 */
export function getFullUrl(path: string): string {
  const baseUrl = siteConfig.url.replace(/\/$/, '')
  const cleanPath = path.startsWith('/') ? path : `/${path}`
  return `${baseUrl}${cleanPath}`
}
